import React, {
  createContext,
  useMemo,
  useState,
  useEffect,
  PropsWithChildren,
} from 'react';
import {
  useQueries,
} from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import isNil from 'lodash/isNil';
import {
  getApiMultimediaGetPatientMediaCategoryDetails,
  getApiSettingsGetAppointmentSettings,
  getApiSettingsGetCcmMonthlyDashboardFilterSettings,
  getApiSettingsGetChargeFilterSettings,
  getApiSettingsGetClaimSettings,
  getApiSettingsGetEFaxFilterSettings,
  getApiSettingsGetEOBFilterSettings,
  getApiSettingsGetInsuranceSettings,
  getApiSettingsGetMediaSettings,
  getApiSettingsGetOrCreateUserSettings,
  getApiSettingsGetPatientPaymentTransactionFilterSettings,
  getApiSettingsGetRTMMonitoringFilterSettings,
  getApiSettingsGetTempoDashboardFilterSettings,
  getApiSettingsGetToDoSettings,
  getApiSettingsGetUserManagementSettings,
  getApiSettingsGetWriteOffSettings,
  getApiFormGetFormBuilderOptions,
} from 'endpoints';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import {
  useGetUserSettings,
} from 'pages/Dashboard/hooks/useUserSettings';
import {
  InsuranceSettingsDTO,
  MediaSettingsDTO,
  PatientMediaCategoryDetailsResponseDTO,
  SettingsDTO,
  ClaimSettingsDTO,
  UserManagementSettingsDTO,
  UserSettingDTO,
  FormBuilderOptionsDTO,
} from 'dtos';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  setDefaultTimeZoneId,
} from 'utils/dayjs';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import {
  useGetAppointmentTypes,
} from 'views/EMR/Settings/hooks';
import {
  useAppointmentStatusLoading,
} from 'views/EMR/stores/appointmentStatus';
import {
  PrefetchedDataProps,
} from 'pages/Dashboard/types/common';
import {
  useDatadog,
} from 'core/hooks';
import {
  useUserActions,
} from 'views/EMR/stores/user';
import {
  useServiceFacilities,
  useServiceFacilityActions,
} from 'views/EMR/stores/serviceFacility';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type Props = {
  settings: Record<string, any>;
  setSettings: (settings: Record<string, any>) => void;
  loadingAppSettings: boolean;
  loadingUserSettings: boolean;
  version: number;
};

export const UserSettingsContext = createContext<Props>({
  settings: {},
  setSettings: () => {},
  version: 0,
  loadingAppSettings: false,
  loadingUserSettings: false,
});

type PrefetchedMetaData = Partial<
  SettingsDTO
  & ClaimSettingsDTO
  & UserSettingDTO
  & UserManagementSettingsDTO
  & FormBuilderOptionsDTO
  & MediaSettingsDTO
  & InsuranceSettingsDTO
  & PatientMediaCategoryDetailsResponseDTO
>;

const prefetchedMetaMap = {
  userViewSettings: {
    key: ['user-view-settings'],
    fetchFn: getApiSettingsGetOrCreateUserSettings,
  },
  chargeFilterSettings: {
    key: ['charge-filter-settings'],
    fetchFn: getApiSettingsGetChargeFilterSettings,
  },
  rtmMonitoringFilterSettings: {
    key: ['rtm-monitoring-filter-settings'],
    fetchFn: getApiSettingsGetRTMMonitoringFilterSettings,
  },
  tempoDashboardFilterSettings: {
    key: ['tempo-dashboard-filter-settings'],
    fetchFn: getApiSettingsGetTempoDashboardFilterSettings,
  },
  ccmMonthlyDashboardFilterSettings: {
    key: ['ccm-monthly-dashboard-filter-settings'],
    fetchFn: getApiSettingsGetCcmMonthlyDashboardFilterSettings,
  },
  eobFilterSettings: {
    key: ['eob-filter-settings'],
    fetchFn: getApiSettingsGetEOBFilterSettings,
  },
  appointmentSettings: {
    key: ['appointment-settings'],
    fetchFn: getApiSettingsGetAppointmentSettings,
  },
  toDoSettings: {
    key: ['to-do-settings'],
    fetchFn: getApiSettingsGetToDoSettings,
  },
  eFaxSettings: {
    key: ['eFax-settings'],
    fetchFn: getApiSettingsGetEFaxFilterSettings,
  },
  patientPaymentTransactionFilterSettings: {
    key: ['patient-payment-transaction-filter-settings'],
    fetchFn: getApiSettingsGetPatientPaymentTransactionFilterSettings,
  },
  claimSettings: {
    key: ['claim-settings'],
    fetchFn: getApiSettingsGetClaimSettings,
  },
  paymentMetadata: {
    key: ['payment-metadata'],
    fetchFn: getApiSettingsGetWriteOffSettings,
  },
  userManagementSettings: {
    key: ['user-settings'],
    fetchFn: getApiSettingsGetUserManagementSettings,
  },
  mediaSettings: {
    key: ['media-settings'],
    fetchFn: getApiSettingsGetMediaSettings,
  },
  insuranceMeta: {
    key: ['insurance-meta'],
    fetchFn: getApiSettingsGetInsuranceSettings,
  },
  patientMediaCategories: {
    key: ['patient-media-categories'],
    fetchFn: getApiMultimediaGetPatientMediaCategoryDetails,
  },
  formBuilderOptions: {
    key: ['form-builder-settings'],
    fetchFn: getApiFormGetFormBuilderOptions,
  },
} satisfies Record<string, PrefetchedDataProps<PrefetchedMetaData>>;

const temporaryAccessEnabledPrefetchKeys = new Set<keyof typeof prefetchedMetaMap>([
  'userViewSettings',
  'appointmentSettings',
  'patientMediaCategories',
  'formBuilderOptions',
]);

function usePrefetchedMetaData() {
  const { userInfo, isRestricted } = useEMRAccess();
  const metaEntries = Object.entries(prefetchedMetaMap);
  const results = useQueries({
    queries: metaEntries.map(([name, meta]) => ({
      queryKey: meta.key,
      queryFn: meta.fetchFn,
      gcTime: Infinity,
      enabled: !isNil(userInfo) && (
        !isRestricted
        || temporaryAccessEnabledPrefetchKeys.has(name as keyof typeof prefetchedMetaMap)
      ),
    })),
  });

  return Object.fromEntries(
    metaEntries.map(([name], index) => [name, results[index]]),
  ) as Record<keyof typeof prefetchedMetaMap, (typeof results)[number]>;
}

export default function UserSettingsProvider({ children }: PropsWithChildren) {
  const userInfo = useUserInfo();

  const { setDatadogUser } = useDatadog();
  const { setPrincipalFacilityState } = useUserActions();

  const { data, isLoading: loadingUserSettings } = useGetUserSettings();
  const { isLoading: loadingDoctors } = useGetUsers();

  const { isLoading: loadingAppointmentSettings } = useGetAppointmentTypes();
  const appointmentStatusLoading = useAppointmentStatusLoading();
  const prefetchedMetaData = usePrefetchedMetaData();
  const loadingMetaDataStates = Object.values(prefetchedMetaData);

  const loadingList = [
    loadingUserSettings,
    loadingDoctors,
    loadingAppointmentSettings,
    appointmentStatusLoading,
    ...map(loadingMetaDataStates, 'isLoading'),
  ];

  const loading = useMemo(() => (
    loadingList.some((isLoading) => isLoading) || isNil(userInfo)
  ), [...loadingList, userInfo]);

  const [settings, setSettings] = useState<Record<string, any>>({});
  const value = useMemo(() => ({
    settings,
    setSettings,
    version: userInfo?.flags?.userSettingsVersion ?? 0,
    loadingAppSettings: loading,
    loadingUserSettings,
  }), [settings, setSettings, userInfo?.flags?.userSettingsVersion, loading, loadingUserSettings]);

  useEffect(() => {
    if (!isEmpty(data) && !loadingUserSettings) {
      const { timeZoneId } = userInfo?.organization ?? {};
      setSettings(JSON.parse(data?.preferenceJson ?? '{}'));
      setDatadogUser(userInfo);
      if (!isNil(timeZoneId)) {
        setDefaultTimeZoneId(timeZoneId);
      }
    }
  }, [data, loadingUserSettings, loading]);

  const serviceFacilities = useServiceFacilities();
  const { fetchServiceFacilities } = useServiceFacilityActions();

  useEffect(() => {
    if (!isNil(userInfo)) {
      fetchServiceFacilities();
    }
  }, [fetchServiceFacilities, userInfo]);

  useEffect(() => {
    if (isNil(userInfo)) {
      return undefined;
    }

    const unsubscribe = settingsEventChannel.on('onServiceFacilityChange', () => {
      fetchServiceFacilities();
    });

    return () => {
      unsubscribe();
    };
  }, [fetchServiceFacilities, userInfo]);

  useEffect(() => {
    if (!isNil(serviceFacilities)) {
      const principalFacility = serviceFacilities.find(
        (serviceFacility) => serviceFacility.isPrincipal,
      );
      setPrincipalFacilityState(principalFacility?.address?.state ?? null);
    }
  }, [serviceFacilities]);

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
}
